import { Injectable } from '@angular/core';
import { DeduplicationFramework } from './deduplication-framework.service';
import { AuthService } from '../auth/auth.service';
import { TelemetryService } from '../telemetry/telemetry.service';
import { ConfigService } from '../config.service';

/**
 * Service Orchestrator - Single initialization point to prevent ALL service duplication.
 * Ensures services are initialized only once, following industry best practices.
 */
@Injectable({ providedIn: 'root' })
export class ServiceOrchestrator {
  private initializationStarted = false;
  private initializationComplete = false;

  constructor(
    private dedup: DeduplicationFramework,
    private authService: AuthService,
    private telemetryService: TelemetryService,
    private configService: ConfigService
  ) {}

  /**
   * Initialize all services once and only once.
   * This method is idempotent - safe to call multiple times.
   */
  async initializeAllServices(): Promise<void> {
    // Use the deduplication framework to ensure single initialization
    return this.dedup.deduplicateAsync('app-init', async () => {
      if (this.initializationStarted) {
        console.log('[ServiceOrchestrator] Initialization already in progress, skipping duplicate call');
        return;
      }

      this.initializationStarted = true;
      console.log('[ServiceOrchestrator] Starting service initialization...');

      try {
        // Initialize services in the correct order
        // Auth must come first as other services depend on it
        await this.initializeAuthService();

        // Telemetry should be initialized early to track other service initializations
        await this.initializeTelemetryService();

        // Note: WebSocket service initialization is handled separately in components as needed

        this.initializationComplete = true;
        console.log('[ServiceOrchestrator] All services initialized successfully');
      } catch (error) {
        console.error('[ServiceOrchestrator] Service initialization failed:', error);
        this.initializationStarted = false; // Allow retry on failure
        throw error;
      }
    }, 60000); // Cache for 1 minute to prevent rapid re-initialization
  }

  /**
   * Initialize authentication service with singleton enforcement
   */
  private async initializeAuthService(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.dedup.ensureSingleInit('auth', () => {
        console.log('[ServiceOrchestrator] Initializing AuthService...');
        this.authService.initialize();
        resolve();
      });
    });
  }

  /**
   * Initialize telemetry service with singleton enforcement
   * FIN-0066: Skip if telemetry is disabled in config
   */
  private async initializeTelemetryService(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.dedup.ensureSingleInit('telemetry', async () => {
        const config = this.configService.getConfig();
        if (config?.telemetry?.enabled) {
          console.log('[ServiceOrchestrator] Initializing TelemetryService...');
          await this.telemetryService.initialize();
        } else {
          console.log('[ServiceOrchestrator] Telemetry disabled in config, skipping initialization');
        }
        resolve();
      });
    });
  }


  /**
   * Check if all services have been initialized
   */
  isInitialized(): boolean {
    return this.initializationComplete;
  }

  /**
   * Get initialization status for monitoring
   */
  getStatus(): {
    started: boolean;
    complete: boolean;
    services: string[];
  } {
    return {
      started: this.initializationStarted,
      complete: this.initializationComplete,
      services: this.dedup.getCacheStats().initializedServices
    };
  }
}